import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { PVODataType, StepsEnum } from 'src/types';

type Data = {
  logo: string;
  pvoData: PVODataType;
  isLoading: boolean;
  activeStep: StepsEnum;
  previousStep: StepsEnum | null;
  hasMedia: boolean;
  recordingStartedByMe: boolean;
  isPreview: boolean;
  playwallId: string | null;
};

type ContextType = Data & {
  setLogo: (logo: string) => void;
  setPvoData: (pvoData: PVODataType) => void;
  setIsLoading: (isLoading: boolean) => void;
  setActiveStep: (activeStep: StepsEnum) => void;
  setRecordingStartedByMe: (recordingStartedByMe: boolean) => void;
  setIsPreview: (isPreview: boolean) => void;
  setPlaywallId: (playwallId: string | null) => void;
};

const initialData: Data = {
  logo: '/logo.svg',
  isLoading: false,
  activeStep: StepsEnum.INITIALIZATION,
  previousStep: null,
  hasMedia: false,
  recordingStartedByMe: false,
  isPreview: false,
  playwallId: null,
  pvoData: {
    id: 0,
    created_at: '',
    score: null,
    hash: '',
    playwall_id: '',
    files: [],
    upload_status: null,
  },
};

export const DataContext = createContext<ContextType | null>(null);

export const DataContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [data, setData] = useState<Data>(initialData);

  const setLogo = useCallback(
    (logo: string) => setData((st) => ({ ...st, logo })),
    []
  );

  const setPvoData = useCallback((pvoData: PVODataType) => {
    setData((st) => ({
      ...st,
      pvoData,
      hasMedia: pvoData.files?.length > 0,
    }));
  }, []);

  const setIsLoading = useCallback((isLoading: boolean) => {
    setData((st) => ({ ...st, isLoading }));
  }, []);

  const setActiveStep = useCallback((activeStep: StepsEnum) => {
    setData((st) => ({
      ...st,
      previousStep: st.activeStep,
      activeStep,
    }));
  }, []);

  const setRecordingStartedByMe = useCallback(
    (recordingStartedByMe: boolean) => {
      setData((st) => ({ ...st, recordingStartedByMe }));
    },
    []
  );

  const setIsPreview = useCallback((isPreview: boolean) => {
    setData((st) => ({ ...st, isPreview }));
  }, []);

  const setPlaywallId = useCallback((playwallId: string | null) => {
    setData((st) => ({ ...st, playwallId }));
  }, []);

  useEffect(() => {
    if (data.hasMedia && data.activeStep !== StepsEnum.RESULT) {
      setData((st) => ({
        ...st,
        activeStep: StepsEnum.RESULT,
      }));
    }
  }, [data.hasMedia, data.activeStep]);

  const value = useMemo(
    () => ({
      ...data,
      setLogo,
      setPvoData,
      setIsLoading,
      setActiveStep,
      setRecordingStartedByMe,
      setIsPreview,
      setPlaywallId,
    }),
    [data]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useDataContext = <T,>(selector: (ctx: ContextType) => T): T => {
  return useContextSelector(DataContext, (ctx) => {
    if (!ctx) {
      throw new Error(
        'useDataContext must be used within a DataContextProvider'
      );
    }
    return selector(ctx);
  });
};
